import { FC, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Button, MenuItem, Stack, TextField } from '@mui/material'

import { Market } from '../../../../../features/markets/types/Market'
import { Store } from '../../../../../features/store/types/Store'
import { emptyFeatureImportValues, sortMarketsByName, toFeatureImportBody } from '../../helpers/importToolHelpers'
import { FeatureImportFormValues } from '../../types/ImportTool'
import { ImportFileField, isCsvFile } from '../ImportFileField/ImportFileField'
import { ImportFlagField } from '../ImportFlagField/ImportFlagField'

type FeatureImportFormProps = {
  stores: Store[]
  markets: Market[]
  isSubmitting?: boolean
  // Resolves true once the import was started, which clears the form.
  onSubmit: (body: FormData) => Promise<boolean>
}

export const FeatureImportForm: FC<FeatureImportFormProps> = ({ stores, markets, isSubmitting, onSubmit }) => {
  const { t } = useTranslation()
  const { control, handleSubmit, reset, formState } = useForm<FeatureImportFormValues>({ mode: 'onChange', defaultValues: emptyFeatureImportValues() })
  const sortedMarkets = useMemo(() => sortMarketsByName(markets), [markets])

  const submit = handleSubmit(async (values) => {
    if (await onSubmit(toFeatureImportBody(values))) reset(emptyFeatureImportValues())
  })

  return (
    <Stack component="form" spacing={2} onSubmit={submit} noValidate>
      <Controller
        name="market"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <TextField {...field} select size="small" label={t('internal-analysts:import_tool_market')} required>
            {sortedMarkets.map((option) => (
              <MenuItem key={option.id} value={option.iso}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>
        )}
      />
      <Controller
        name="store"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <TextField {...field} select size="small" label={t('internal-analysts:import_tool_store')} required>
            {stores.map((option) => (
              <MenuItem key={option.id} value={option.name}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>
        )}
      />
      <Controller
        name="test"
        control={control}
        rules={{ required: true }}
        render={({ field }) => <ImportFlagField label={t('internal-analysts:import_tool_test')} value={field.value} onChange={field.onChange} />}
      />
      <Controller
        name="file"
        control={control}
        rules={{ validate: (file) => !!file && isCsvFile(file) }}
        render={({ field }) => <ImportFileField label={t('internal-analysts:import_tool_features_file')} value={field.value} onChange={field.onChange} />}
      />
      <Stack direction="row">
        <Button type="submit" variant="contained" disabled={!formState.isValid || isSubmitting}>
          {t('internal-analysts:import_tool_upload_features')}
        </Button>
      </Stack>
    </Stack>
  )
}
